import { ResourceType } from "@istari-frontend/resource/model";

import { SystemResourceType } from "~/enums";
import type { AccessResolution, ResourceNode } from "~/systems/model/directory-node";
import { isReadmeListedRevision } from "~/systems/model/readme-resource";
import type {
  TrackedFileAccess,
  TrackedFileAccessOptions,
  UnavailableAccess,
  UnavailableTrackedFile,
} from "~/systems/model/tracked-file-access";

/**
 * Splits a snapshot's revision listing against the tracked files of the configuration it was
 * taken from: every tracked file the listing returned is accessible. An omitted tracked file
 * only counts as denied once `isRevisionsComplete` says the listing is whole — while pages are
 * still arriving an omission reads as loading, since a partial listing cannot prove a denial.
 * A `trackedFiles` of `undefined` means the comparison set has not arrived yet, so nothing can
 * be told apart from a denial and every listed revision reads as loading.
 */
export function resolveTrackedFileAccess<TRevision extends ListedRevision>(
  revisions: readonly TRevision[],
  trackedFiles: readonly TrackedFileRef[] | undefined,
  { isRevisionsComplete, includeFileResources }: TrackedFileAccessOptions,
): TrackedFileAccess<TRevision> {
  const directoryRevisions = revisions.filter((revision) => isDirectoryResourceType(revision, includeFileResources));

  if (!trackedFiles) {
    return { listed: directoryRevisions.map((revision) => ({ revision, access: LOADING })), unavailable: [] };
  }

  const trackedByFileId = new Map(trackedFiles.map((trackedFile) => [trackedFile.fileId, trackedFile]));
  const listedFileIds = new Set(revisions.flatMap((revision) => (revision.fileId ? [revision.fileId] : [])));
  // A partially loaded listing cannot prove a denial, so an omission still reads as loading.
  const unavailableAccess: UnavailableAccess = isRevisionsComplete ? INACCESSIBLE : LOADING;

  return {
    listed: directoryRevisions.flatMap((revision) => {
      const trackedFile = revision.fileId ? trackedByFileId.get(revision.fileId) : undefined;
      return trackedFile ? [{ revision, access: ACCESSIBLE, path: trackedFile.path }] : [];
    }),
    unavailable: [...trackedByFileId.values()]
      .filter((trackedFile) => !listedFileIds.has(trackedFile.fileId))
      .map((trackedFile) => ({
        fileId: trackedFile.fileId,
        revisionId: trackedFile.currentFileRevisionId || trackedFile.fileId,
        resourceId: trackedFile.resourceId ?? undefined,
        access: unavailableAccess,
        path: trackedFile.path,
      })),
  };
}

export function toUnavailableResourceNode({
  revisionId,
  resourceId,
  access,
  path,
}: UnavailableTrackedFile): ResourceNode {
  return {
    id: revisionId,
    name: access.status === "loading" ? LOADING_FILE_LABEL : INACCESSIBLE_FILE_LABEL,
    kind: "resource",
    resourceId,
    revisionId,
    typeName: ResourceType.File,
    access,
    hasChildren: false,
    path,
  };
}

type ListedRevision = {
  readonly fileId?: string | null;
  readonly resourceType?: string | null;
  readonly displayName?: string | null;
  readonly name?: string | null;
  readonly extension?: string | null;
};

type TrackedFileRef = {
  readonly fileId: string;
  readonly currentFileRevisionId?: string | null;
  readonly resourceId?: string | null;
  readonly path?: string;
};

const ACCESSIBLE: AccessResolution = { status: "resolved", value: "accessible" };
const INACCESSIBLE: UnavailableAccess = { status: "resolved", value: "inaccessible" };
const LOADING: UnavailableAccess = { status: "loading" };

const INACCESSIBLE_FILE_LABEL = "Inaccessible resource";
const LOADING_FILE_LABEL = "Loading…";

function isDirectoryResourceType(revision: ListedRevision, includeFileResources: boolean): boolean {
  const resourceType = revision.resourceType;
  if (resourceType === SystemResourceType.Model || resourceType === SystemResourceType.Artifact) {
    return true;
  }
  if (resourceType === SystemResourceType.File) {
    return includeFileResources || isReadmeListedRevision(revision);
  }
  return false;
}
